import { useEffect, useRef, useState } from "react";
import ApiService from "../../../services/api-service";
import notifier from "../../Notifier";
import { getErrorString, getUserId } from "../../../constants/function";

/**
 * Follow / unfollow for the live viewer list.
 *
 * followState maps userId -> true | false. A viewer has no entry until their
 * status loads, so a button never shows the wrong label. Status reloads each
 * time the list opens or its roster changes, so a follow made elsewhere (the
 * host Follow button, a profile page) shows up.
 */
export default function useViewerFollows(watchers, isOpen) {
  const [followState, setFollowState] = useState({});
  // The ref is the source of truth (a second click lands before a re-render);
  // the state copy re-renders the disabled buttons.
  const pendingRef = useRef(new Set());
  const [pending, setPending] = useState(pendingRef.current);

  const markPending = (id, on) => {
    const next = new Set(pendingRef.current);
    if (on) next.add(id);
    else next.delete(id);
    pendingRef.current = next;
    setPending(next);
  };

  const me = String(getUserId() || "");
  const idsKey = [...new Set((watchers || []).map((w) => w?.user?._id).filter(Boolean).map(String))]
    .filter((id) => id !== me)
    .sort()
    .join(",");

  useEffect(() => {
    if (!isOpen || !idsKey) return undefined;
    const ids = idsKey.split(",");
    let cancelled = false;
    ApiService.FOLLOW_STATUS(ids)
      .then((res) => {
        if (cancelled) return;
        const followed = new Set(res?.result?.following || []);
        setFollowState((prev) => {
          const next = { ...prev };
          // A result that lands mid-toggle would undo the click.
          for (const id of ids) {
            if (!pendingRef.current.has(id)) next[id] = followed.has(id);
          }
          return next;
        });
      })
      .catch((e) => {
        console.warn("[useViewerFollows] follow status failed:", e?.response?.data?.message || e?.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, idsKey]);

  const toggleFollow = async (userId) => {
    const id = String(userId || "");
    if (!id || pendingRef.current.has(id) || followState[id] === undefined) return;
    const wasFollowing = followState[id];
    markPending(id, true);
    setFollowState((prev) => ({ ...prev, [id]: !wasFollowing }));
    try {
      const payload = { following: id, status: !wasFollowing };
      if (wasFollowing) await ApiService.UNFOLLOW(payload);
      else await ApiService.FOLLOW(payload);
    } catch (e) {
      setFollowState((prev) => ({ ...prev, [id]: wasFollowing }));
      notifier.error(getErrorString(e));
    } finally {
      markPending(id, false);
    }
  };

  return {
    followState,
    isFollowPending: (userId) => pending.has(String(userId)),
    toggleFollow,
  };
}
